import clsx from 'clsx';
import { CheckCircle2, Info, XCircle } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

type ToastTone = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastContextValue {
  toast: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);
let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((message: string, tone: ToastTone = 'success') => {
    const id = nextId++;
    setItems((current) => [...current.slice(-3), { id, tone, message }]);
    window.setTimeout(
      () => setItems((current) => current.filter((t) => t.id !== id)),
      tone === 'error' ? 6000 : 3500,
    );
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);
  const icons = { success: CheckCircle2, error: XCircle, info: Info };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-20 z-50 flex flex-col items-center gap-2 px-4 md:bottom-6"
      >
        {items.map((t) => {
          const Icon = icons[t.tone];
          return (
            <div
              key={t.id}
              className={clsx(
                'clip-corner-sm pointer-events-auto flex max-w-md items-start gap-2 border px-4 py-3 text-sm shadow-xl',
                t.tone === 'success' && 'border-success/40 bg-ink-850 text-bone',
                t.tone === 'error' && 'border-danger/50 bg-ink-850 text-bone',
                t.tone === 'info' && 'border-ink-600 bg-ink-850 text-bone',
              )}
            >
              <Icon
                className={clsx(
                  'mt-0.5 size-4 shrink-0',
                  t.tone === 'success'
                    ? 'text-success'
                    : t.tone === 'error'
                      ? 'text-danger'
                      : 'text-ink-300',
                )}
              />
              <span>{t.message}</span>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
